import { useMarketStore } from '@/stores/Market.store';
import {READY_GATE} from '@/services/bootstrap';

export default function mapServiceFactory(services) {
  let thisService = {
    preInitAsync,
    initAsync,
    getProductsAsync,
    getProducts,
    getSubscriptionsAsync,
    getSubscriptions,
    getProduct,
    hasProduct,
    getSubscription,
    hasSubscription,
    hasActiveSubscription,
  };

  let marketStore;

  return thisService;

  ////////////////////
  async function preInitAsync() {
    marketStore = useMarketStore();
    return Promise.resolve();
  }

  async function initAsync() {
    if (!services.session.isAuthenticated() || services.session.isHP()) {
      services.bootstrap.readyGateCleared(READY_GATE.market);
      return Promise.resolve();
    }

    // Only wait for marketplace APIs in marketplace
    if (services.app.getCurrentAppId() !== 'marketplace') {
      services.bootstrap.readyGateCleared(READY_GATE.market);
    }

    return getPermissionsAsync().then(permissions => {
      let promises = [];
      if (permissions.includes('Retrieve')) {
        promises.push(getProductsAsync(true));
        promises.push(getSubscriptionsAsync(true));
      }
      return Promise.all(promises).then(() => {
        evaluateTriggers();
      });
    })
      .catch(() => {
        // Probably means we aren't logged in, so it's ok we aren't loading market stuff
        return Promise.resolve();
      })
      .finally(() => {
        services.bootstrap.readyGateCleared(READY_GATE.market);
      });
  }

  function getPermissionsAsync() {
    return services.session.HTTPGet('product_service', 'permissions').then(resp => {
      return resp.data;
    });

  }

  function getProductsAsync(refresh = false) {
    if (!refresh && marketStore.products) {
      return Promise.resolve(marketStore.products);
    }

    if (marketStore.productsPromise) {
      return marketStore.productsPromise;
    }

    let productPromise = services.session.HTTPGet('product_service', 'organizations/products').then(resp => {
      let products = resp.data?.products ? resp.data.products : null;
      if (!products) {
        return;
      }

      marketStore.setProducts(products);
      return products;
    });
    marketStore.setProductsPromise(productPromise);
    return productPromise;
  }

  function getProducts() {
    return marketStore.products ? marketStore.products : [];
  }

  function getProduct(productCode) {
    if (!marketStore.products) {
      return null;
    }

    return marketStore.products.find(p => { return p.productCode === productCode });
  }

  function hasProduct(productCode) {
    if (!marketStore.products) {
      return false;
    }

    return !!marketStore.products.find(p => {
      return p.productCode === productCode
    });
  }

  function getSubscription(productCode) {
    if (!marketStore.subscriptions) {
      return false;
    }

    let latestSubscriptions = _getLatestSubscriptions(marketStore.subscriptions);

    return latestSubscriptions.find(s => { return s.productCode === productCode });
  }

  function hasSubscription(productCode) {
    if (!marketStore.subscriptions) {
      return false;
    }

    let latestSubscriptions = _getLatestSubscriptions(marketStore.subscriptions);

    return latestSubscriptions.find(s => { return s.productCode === productCode }) ? true : false;
  }

  function hasActiveSubscription(productCode) {
    if (!marketStore.subscriptions) {
      return false;
    }

    let latestSubscriptions = _getLatestSubscriptions(marketStore.subscriptions);

    return latestSubscriptions.find(s => { return s.productCode === productCode && s.status === 'ACTIVE' }) ? true : false;
  }

  function getSubscriptionsAsync(refresh = false, latest = true) {
    if (!refresh && marketStore.subscriptions) {
      if (latest) {
        return _getLatestSubscriptions(marketStore.subscriptions);
      }

      return Promise.resolve(marketStore.subscriptions);
    }

    return services.session.HTTPGet('product_service', 'organizations/subscriptions').then(resp => {
      let subscriptions = resp.data?.subscriptions ? resp.data.subscriptions : null;
      if (!subscriptions) {
        return;
      }

      marketStore.setSubscriptions(subscriptions);
    });
  }

  function getSubscriptions() {
    return marketStore.subscriptions ? marketStore.subscriptions : [];
  }

  function _getLatestSubscriptions(subscriptions) {
    let productCodes = subscriptions.map(s => {
      return s.productCode;
    });
    productCodes = [... new Set(productCodes)];

    let subs = [];
    productCodes.forEach(pc => {
      let matchingSubs = subscriptions.filter(s => {
        return s.productCode === pc;
      });

      let latest = null;
      matchingSubs.forEach(s => {
        if (!latest || s.createdTime > latest.createdTime) {
          latest = s;
        }
      });

      subs.push(latest);
    });

    return subs;
  }

  function evaluateTriggers() {
    let printBeatPremiumFeature = services.session.getEntitlementFeature('printbeat-premium');
    if (printBeatPremiumFeature && services.session.isIndigo()) {
      let autoGrantedDeviceIds = printBeatPremiumFeature.autoIncludedDeviceIds ? printBeatPremiumFeature.autoIncludedDeviceIds : [];
      let grantableDeviceIds = printBeatPremiumFeature.grantableDeviceIds ? printBeatPremiumFeature.grantableDeviceIds : [];
      let grantedDeviceIds = printBeatPremiumFeature.grantedDeviceIds ? printBeatPremiumFeature.grantedDeviceIds : [];
      let partiallySubscribed = grantableDeviceIds.length > 0 && grantableDeviceIds.length > grantedDeviceIds.length;
      if (partiallySubscribed) {
        let preEnforcementTitle = services.i18n.$t('header.marketplace.partialProductivitySubscribePreEnforcement_Title');
        // Horrible hack because our localization process cannot handle // in a string (treats as comment).  We need to make sure we restore it
        // or the browser adds the domain to the link
        if (preEnforcementTitle.includes('https:/') && !preEnforcementTitle.includes('https://')) {
          preEnforcementTitle = preEnforcementTitle.replace('https:/', 'https://');
        }
        let preEnforcementDesc = services.i18n.$t('header.marketplace.partialProductivitySubscribePreEnforcement_Desc');
        let postEnforcementTitle = services.i18n.$t('header.marketplace.partialProductivitySubscribePostEnforcement_Title', [grantedDeviceIds.length, grantableDeviceIds.length]);
        let postEnforcementDesc = services.i18n.$t('header.marketplace.partialProductivitySubscribePostEnforcement_Desc');

        let today = new Date();
        let enforcementDate = new Date("2025-08-04");
        //let enforcementDate = new Date("2025-04-21");
        // Before the enforcement date, show the pre-enforcement alert
        if (today < enforcementDate) {
          services.ui.addInlineAlert({
            id: `productivity-partial-subscription-pre-enforcement-${services.session.getOrgId()}`,
            title: preEnforcementTitle,
            description: preEnforcementDesc,
            severity: 'warn',
            // icon: 'document-signature',
            cooldown: 24 * 60 * 60 * 1000,
            actions: [
              {
                type: 'button',
                buttonType: 'ghost',
                title: services.i18n.$t('header.marketplace.howToExtract_Action'),
                callback: () => {
                  services.ga.eventTrack('home', 'Print Beat Premium How to Extract Link Clicked', 'Pre enforcement');
                  services.go.toContext('kz-asset', '764909');
                },
              },
              {
                type: 'button',
                title: services.i18n.$t('header.marketplace.goToMarket_Action'),
                callback: () => {
                  services.ga.eventTrack('home', 'Print Beat Premium Alert Button Clicked', 'Pre enforcement');
                  services.go.toContext('market-product', 'productivity-quality-pack');
                },
              }
            ],
          });
        } else {
          // After the enforcement date, show the post-enforcement (partially subscribed) alert until we set an end date or remove the alert
          services.ui.addInlineAlert({
            id: `productivity-partial-subscription-post-enforcement-${services.session.getOrgId()}`,
            title: postEnforcementTitle,
            description: postEnforcementDesc,
            severity: 'warn',
            // icon: 'document-signature',
            cooldown: 24 * 60 * 60 * 1000,
            actions: [
              {
                type: 'button',
                title: services.i18n.$t('header.marketplace.goToMarket_Action'),
                callback: () => {
                  services.ga.eventTrack('home', 'Print Beat Premium Alert Button Clicked', 'Post enforcement');
                  services.go.toContext('market-product', 'productivity-quality-pack');
                },
              }
            ],
          });
        }
      }
    }
  }
}
